/* ============================================================
   Page collection — logique pure (aucun accès au DOM) :
   normalisation des valeurs faciales / qualités / pays / millésimes,
   comparateurs de tri (règles par collection, menu « Trier »),
   facettes avec compteurs, filtrage, regroupement par année,
   lecture / écriture de l'état dans l'URL.
   Chargé dans le navigateur (window.MDMCollection) et testé sous Node
   (node scripts/test-collection.js).

   TRI PAR DÉFAUT — toujours par année (de la plus récente à la plus ancienne), puis, dans chaque année,
   une règle qui dépend de la collection (modeDepuisCollection) et du régime de l'année :
     - année en cours ou à venir (heure de Paris) : « en cours » ;
     - année terminée : « terminée » (bascule automatique le 1er janvier).
   Qualités : UNC → FDC → BU → BE → Polissage inversé. Métal : non précieux → argent → or.
   « Pièce par pièce » : les pièces les plus récemment émises d'abord, chaque pièce regroupée avec ses qualités.
   ============================================================ */
(function (racine, fabrique) {
  var api = fabrique();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else racine.MDMCollection = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ---------- texte ---------- */
  function nettoyer(t) {
    return String(t == null ? '' : t).replace(/[   ]/g, ' ').replace(/\s+/g, ' ').trim();
  }
  function sansAccent(t) {
    return nettoyer(t).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  }
  var collateur = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });
  function comparerTexte(a, b) { return collateur.compare(a || '', b || ''); }

  var NBSP = ' ';

  /* ---------- année en cours, heure de Paris ---------- */
  function anneeParis(maintenant) {
    var d = maintenant ? new Date(maintenant) : new Date();
    try {
      return parseInt(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Paris', year: 'numeric' }).format(d), 10);
    } catch (e) { return d.getFullYear(); }
  }
  var ANNEE_COURANTE = anneeParis();

  /* ---------- valeur faciale ----------
     « 2 € », « 3,88 € », « 50 c », « 5 $ CAD », « 500 ¥ », « 1/10 Krugerrand »…
     → { cle, libelle, nombre (équivalent numérique, ordre croissant), devise } */
  function formaterNombre(n) {
    if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n));
    return n.toFixed(2).replace('.', ',');
  }
  function analyserValeur(brut) {
    var t = nettoyer(brut);
    var vide = { cle: '', libelle: '', nombre: Infinity, devise: '', brut: t };
    if (!t) return vide;
    var nombreTxt = '';
    var reste = '';
    var m = /^(\d+\s*\/\s*\d+|\d+(?:[.,]\d+)?)\s*(.*)$/.exec(t);
    if (m) { nombreTxt = m[1]; reste = m[2]; }
    else {
      var m2 = /^([€$£¥])\s*(\d+(?:[.,]\d+)?)\s*(.*)$/.exec(t);
      if (m2) { nombreTxt = m2[2]; reste = (m2[1] + ' ' + m2[3]).trim(); }
      else return { cle: sansAccent(t), libelle: t, nombre: Infinity, devise: t, brut: t };
    }
    var nombre;
    var libNombre;
    if (nombreTxt.indexOf('/') > -1) {
      var f = nombreTxt.split('/');
      nombre = parseFloat(f[0]) / parseFloat(f[1]);
      libNombre = f[0].trim() + '/' + f[1].trim();
    } else {
      nombre = parseFloat(nombreTxt.replace(',', '.'));
      libNombre = formaterNombre(nombre);
    }
    var d = reste.trim();
    var dn = sansAccent(d);
    var devise = d;
    var equiv = nombre;
    if (/^(€|eur|euro|euros)$/.test(dn)) devise = '€';
    else if (/^(c|ct|cts|cent|cents|centime|centimes)$/.test(dn)) { devise = 'c'; equiv = nombre / 100; }
    else if (/^\$\s*[a-z]{2,3}$/.test(dn)) devise = '$ ' + dn.replace(/^\$\s*/, '').toUpperCase();
    else if (/^[a-z]{3}$/.test(dn) && dn !== 'oz') devise = dn.toUpperCase();
    var cle = devise === 'c' || devise === '€' ? formaterNombre(equiv) + '|€' : formaterNombre(nombre) + '|' + sansAccent(devise);
    var affichage = devise === '€' ? 'Euro' : devise === 'c' ? (nombre > 1 ? 'centimes' : 'centime') : devise;
    var libelle = libNombre + (affichage ? NBSP + affichage : '');
    return { cle: cle, libelle: libelle, nombre: equiv, devise: devise, brut: t };
  }
  function ordreDevise(d) { return d === '€' || d === 'c' ? '' : d; }
  function comparerValeurs(a, b) {
    var inf = a.nombre === Infinity, infb = b.nombre === Infinity;
    if (inf && infb) return comparerTexte(a.libelle, b.libelle);
    if (inf) return 1;
    if (infb) return -1;
    if (Math.abs(a.nombre - b.nombre) > 1e-9) return a.nombre - b.nombre;
    return comparerTexte(ordreDevise(a.devise), ordreDevise(b.devise));
  }

  /* ---------- qualité : UNC, FDC, BU, BE, Polissage inversé puis le reste ; « BU colorisée » juste après « BU » ---------- */
  var BASES_QUALITE = [
    { cle: 'unc', libelle: 'UNC', motif: /^(unc|uncirculated|non circulee?)\b/ },
    { cle: 'fdc', libelle: 'FDC', motif: /^(fdc|fleur de coin)\b/ },
    { cle: 'bu', libelle: 'BU', motif: /^(bu|brillant universel|brilliant uncirculated|bunc)\b/ },
    { cle: 'be', libelle: 'BE', motif: /^(be|belle epreuve|proof)\b/ },
    { cle: 'pi', libelle: 'Polissage inversé', motif: /^(polissage inverse|pi|reverse proof)\b/ }
  ];
  function analyserQualite(brut) {
    var t = nettoyer(brut);
    if (!t) return { cle: '', libelle: '', rang: 99, sous: '' };
    var n = sansAccent(t);
    for (var i = 0; i < BASES_QUALITE.length; i++) {
      var m = BASES_QUALITE[i].motif.exec(n);
      if (m) {
        var sous = n.slice(m[0].length).trim();
        var libelle = sous ? t : BASES_QUALITE[i].libelle;
        return { cle: n, libelle: libelle, rang: i, sous: sous };
      }
    }
    return { cle: n, libelle: t, rang: BASES_QUALITE.length, sous: n };
  }
  function comparerQualites(a, b) {
    if (a.rang !== b.rang) return a.rang - b.rang;
    if (a.sous !== b.sous) {
      if (!a.sous) return -1;
      if (!b.sous) return 1;
      return comparerTexte(a.sous, b.sous);
    }
    return 0;
  }

  /* ---------- métal : non précieux (0) → argent (1) → or (2) ---------- */
  function rangMetal(brut) {
    var n = sansAccent(brut);
    if (!n) return 0;
    if (/(^|[^a-z])or([^a-z]|$)/.test(n)) return 2;
    if (n.indexOf('argent') > -1) return 1;
    return 0;
  }

  /* ---------- « pièce » : le titre sans la qualité, pour regrouper les qualités d'une même pièce ---------- */
  var MOTS_QUALITE = /\b(unc|fdc|bu|be|bunc|pi|polissage inverse|belle epreuve|brillant universel|fleur de coin|proof|reverse proof)\b/g;
  function clePiece(titre) {
    return sansAccent(titre).replace(MOTS_QUALITE, ' ').replace(/\s+/g, ' ').trim();
  }

  /* ---------- types (tags type:2euro | serie | nc | monde) ---------- */
  var TYPE_RANG = { '2euro': 0, serie: 1, nc: 2, monde: 3 };
  function rangType(t) { return Object.prototype.hasOwnProperty.call(TYPE_RANG, t) ? TYPE_RANG[t] : 4; }

  /* ---------- enregistrement normalisé d'un produit ---------- */
  function creerRecord(d, ordre) {
    var annee = parseInt(d.millesime, 10);
    if (!(annee > 0)) annee = 0;
    var pays = nettoyer(d.pays);
    var type = sansAccent(d.type);
    var titre = nettoyer(d.titre);
    return {
      id: d.id || '',
      ordre: ordre == null ? 0 : ordre,
      pays: pays,
      paysCle: sansAccent(pays),
      valeur: analyserValeur(d.valeur),
      qualite: analyserQualite(d.qualite),
      annee: annee,
      type: type,
      typeRang: rangType(type),
      titre: titre,
      piece: clePiece(titre),
      cleTri: d.cleTri || '',
      prix: +d.prix || 0,
      date: +d.date || 0,
      emission: +d.emission || +d.date || 0,
      metal: rangMetal(d.metal),
      html: d.html || '',
      el: d.el || null
    };
  }

  /* ---------- règle de tri selon la collection ----------
     Pour changer la règle d'une collection, ajoutez son adresse (handle) ici. */
  var PAYS_EURO = ['allemagne', 'andorre', 'autriche', 'belgique', 'bulgarie', 'chypre', 'croatie', 'espagne', 'estonie',
    'finlande', 'france', 'grece', 'irlande', 'italie', 'lettonie', 'lituanie', 'luxembourg', 'malte', 'monaco',
    'pays-bas', 'portugal', 'saint-marin', 'slovaquie', 'slovenie', 'vatican'];
  function modeDepuisCollection(handle) {
    var h = String(handle || '').toLowerCase();
    if (h === '2-euro') return 'deuxeuro';
    if (/^2-euro-\d{4}$/.test(h)) return 'deuxeuro_annee';
    if (h === '2-euro-bu' || h === '2-euro-be') return 'deuxeuro_bube';
    if (h === '2-euro-nationale') return 'deuxeuro_nat';
    if (/^2-euro-/.test(h)) return 'deuxeuro_pays';
    if (h === 'euro' || /^\d{4}$/.test(h)) return 'euro';
    if (PAYS_EURO.indexOf(h) > -1) return 'euro_pays';
    if (h === 'serie' || /^serie-\d{4}$/.test(h)) return 'serie';
    if (h === 'serie-bu' || h === 'serie-be') return 'serie_bube';
    if (/^serie-/.test(h)) return 'serie_pays';
    if (h === 'commemorative-non-circulante-1' || /^commemorative-non-circulante-\d{4}$/.test(h)) return 'nc';
    if (h === 'commemorative-non-circulante' || /^commemorative-non-circulante-/.test(h)) return 'nc_pays';
    return '';
  }

  /* ---------- comparateurs ---------- */
  function comparerAnnees(a, b) {
    if (a.annee === b.annee) return 0;
    if (!a.annee) return 1;
    if (!b.annee) return -1;
    return b.annee - a.annee;
  }
  function departage(a, b) {
    return comparerTexte(a.titre, b.titre) || (a.ordre - b.ordre);
  }
  function parEmission(a, b) { return b.emission - a.emission; }
  function parQualite(a, b) { return comparerQualites(a.qualite, b.qualite); }
  function parPays(a, b) { return comparerTexte(a.pays, b.pays); }
  function parValeur(a, b) { return comparerValeurs(a.valeur, b.valeur); }
  function parMetal(a, b) { return a.metal - b.metal; }
  /* pièce par pièce : la plus récemment émise d'abord, puis ses qualités */
  function pieceParPiece(a, b) {
    return parEmission(a, b) || comparerTexte(a.piece, b.piece) || parQualite(a, b);
  }
  /* par type : 2 Euro (pièce par pièce) → Séries (qualité) → Commémoratives NC (valeur, qualité) → le reste */
  function parType(a, b) {
    var c = a.typeRang - b.typeRang;
    if (c) return c;
    if (a.typeRang === 0) return pieceParPiece(a, b);
    if (a.typeRang === 1) return parQualite(a, b) || parPays(a, b);
    if (a.typeRang === 2) return parValeur(a, b) || parQualite(a, b) || parMetal(a, b);
    return parPays(a, b) || parValeur(a, b) || parQualite(a, b);
  }
  function enchainer(liste) {
    return function (a, b) {
      for (var i = 0; i < liste.length; i++) { var c = liste[i](a, b); if (c) return c; }
      return departage(a, b);
    };
  }
  /* [année en cours, année terminée] pour chaque règle */
  var REGLES = {
    deuxeuro:       [enchainer([parEmission, parQualite]), enchainer([parPays, pieceParPiece])],
    deuxeuro_annee: [enchainer([parEmission, parQualite]), enchainer([parPays, pieceParPiece])],
    deuxeuro_pays:  [enchainer([parEmission, parQualite]), enchainer([pieceParPiece])],
    deuxeuro_bube:  [enchainer([parEmission, parQualite]), enchainer([parPays, parQualite])],
    deuxeuro_nat:   [enchainer([parPays, parQualite]), enchainer([parPays, parQualite])],
    euro:           [enchainer([parEmission, parQualite]), enchainer([parPays, parType])],
    euro_pays:      [enchainer([parType]), enchainer([parType])],
    nc:             [enchainer([parEmission, parQualite]), enchainer([parPays, parValeur, parQualite])],
    nc_pays:        [enchainer([parEmission, parQualite, parMetal]), enchainer([parValeur, parQualite, parMetal])],
    serie:          [enchainer([parEmission, parQualite]), enchainer([parPays, parQualite])],
    serie_pays:     [enchainer([parQualite]), enchainer([parQualite])],
    serie_bube:     [enchainer([parEmission, parQualite]), enchainer([parPays, parQualite])]
  };
  function regleDansAnnee(mode) {
    var r = REGLES[mode];
    if (!r) return null;
    return function (a, b) {
      var terminee = a.annee > 0 && a.annee < ANNEE_COURANTE;
      return r[terminee ? 1 : 0](a, b);
    };
  }

  /* règle historique : dans une année, 2 € (UNC, BU, BE) → Séries (BU, BE) → Commémoratives NC (valeur ↑) → le reste */
  function comparerDansAnneeTypeQualite(a, b) {
    var c = a.typeRang - b.typeRang;
    if (c) return c;
    if (a.typeRang === 2) {
      return comparerValeurs(a.valeur, b.valeur) || comparerQualites(a.qualite, b.qualite) || comparerTexte(a.pays, b.pays) || departage(a, b);
    }
    if (a.typeRang <= 1) {
      return comparerQualites(a.qualite, b.qualite) || comparerTexte(a.pays, b.pays) || comparerValeurs(a.valeur, b.valeur) || departage(a, b);
    }
    return comparerTexte(a.pays, b.pays) || comparerValeurs(a.valeur, b.valeur) || comparerQualites(a.qualite, b.qualite) || departage(a, b);
  }
  function comparerDansAnneeAlpha(a, b) {
    return departage(a, b);
  }
  function comparateur(tri, mode) {
    switch (tri) {
      case 'price-ascending': return function (a, b) { return (a.prix - b.prix) || departage(a, b); };
      case 'price-descending': return function (a, b) { return (b.prix - a.prix) || departage(a, b); };
      case 'title-ascending': return departage;
      case 'created-descending': return function (a, b) { return (b.date - a.date) || departage(a, b); };
      default:
        var dedans = regleDansAnnee(mode) || (mode === 'type-qualite' ? comparerDansAnneeTypeQualite : comparerDansAnneeAlpha);
        return function (a, b) { return comparerAnnees(a, b) || dedans(a, b); };
    }
  }
  /* un tri du menu « Trier » remplace le tri par défaut : plus de regroupement par année */
  function estTriParDefaut(tri) {
    return !tri || tri === 'manual' || tri === 'defaut';
  }
  function trier(liste, tri, mode) {
    return liste.slice().sort(comparateur(tri, mode));
  }

  /* ---------- regroupement visuel : années, puis (option) types ---------- */
  function grouper(liste, sousTitresType) {
    var annees = [];
    var courante = null;
    liste.forEach(function (r) {
      if (!courante || courante.annee !== r.annee) {
        courante = { annee: r.annee, groupes: [] };
        annees.push(courante);
      }
      var cleType = sousTitresType ? (r.typeRang < 4 ? r.type : 'autres') : '';
      var g = courante.groupes[courante.groupes.length - 1];
      if (!g || g.type !== cleType) {
        g = { type: cleType, items: [] };
        courante.groupes.push(g);
      }
      g.items.push(r);
    });
    return annees;
  }

  /* ---------- facettes ---------- */
  var FACETTES = ['valeur', 'pays', 'qualite', 'annee'];

  function cleFacette(r, f) {
    switch (f) {
      case 'valeur': return r.valeur.cle || null;
      case 'pays': return r.paysCle || null;
      case 'qualite': return r.qualite.cle || null;
      case 'annee': return r.annee ? String(r.annee) : null;
    }
    return null;
  }
  function libelleFacette(r, f) {
    switch (f) {
      case 'valeur': return r.valeur.libelle;
      case 'pays': return r.pays;
      case 'qualite': return r.qualite.libelle;
      case 'annee': return String(r.annee);
    }
    return '';
  }
  function comparerOptions(f) {
    return function (a, b) {
      switch (f) {
        case 'valeur': return comparerValeurs(a.ref.valeur, b.ref.valeur);
        case 'pays': return comparerTexte(a.libelle, b.libelle);
        case 'qualite': return comparerQualites(a.ref.qualite, b.ref.qualite) || comparerTexte(a.libelle, b.libelle);
        case 'annee': return b.ref.annee - a.ref.annee;
      }
      return 0;
    };
  }

  function selectionVide() {
    var s = {};
    FACETTES.forEach(function (f) { s[f] = new Map(); });
    return s;
  }
  function nbFiltres(sel) {
    var n = 0;
    FACETTES.forEach(function (f) { n += sel[f].size; });
    return n;
  }
  function correspond(r, sel, ignorer) {
    for (var i = 0; i < FACETTES.length; i++) {
      var f = FACETTES[i];
      if (f === ignorer || !sel[f].size) continue;
      var c = cleFacette(r, f);
      if (c === null || !sel[f].has(c)) return false;
    }
    return true;
  }
  function filtrer(records, sel) {
    if (!nbFiltres(sel)) return records;
    return records.filter(function (r) { return correspond(r, sel, null); });
  }
  /* options de chaque facette ; compteurs calculés avec les AUTRES facettes appliquées
     (on peut ainsi cocher plusieurs valeurs d'une même facette) */
  function calculerFacettes(records, sel) {
    var sortie = {};
    FACETTES.forEach(function (f) {
      var options = new Map();
      records.forEach(function (r) {
        var c = cleFacette(r, f);
        if (c === null) return;
        var o = options.get(c);
        if (!o) { o = { cle: c, libelle: libelleFacette(r, f), compte: 0, ref: r, selectionne: sel[f].has(c) }; options.set(c, o); }
        if (correspond(r, sel, f)) o.compte++;
      });
      /* valeurs sélectionnées (URL) qui n'existent pas (encore) dans les données */
      sel[f].forEach(function (lib, c) {
        if (!options.has(c)) options.set(c, { cle: c, libelle: lib, compte: 0, ref: refDepuisLibelle(f, lib), selectionne: true });
      });
      sortie[f] = Array.from(options.values()).sort(comparerOptions(f));
    });
    return sortie;
  }
  function refDepuisLibelle(f, lib) {
    return { valeur: analyserValeur(f === 'valeur' ? lib : ''), qualite: analyserQualite(f === 'qualite' ? lib : ''), annee: f === 'annee' ? parseInt(lib, 10) || 0 : 0 };
  }

  /* ---------- état ⇄ URL (?pays=France&qualite=BE&annee=2025&sort_by=…) ---------- */
  var PARAMS = { valeur: 'valeur', pays: 'pays', qualite: 'qualite', annee: 'annee' };
  var TRIS = ['price-ascending', 'price-descending', 'title-ascending', 'created-descending'];

  function cleDepuisLibelle(f, lib) {
    switch (f) {
      case 'valeur': return analyserValeur(lib).cle;
      case 'pays': return sansAccent(lib);
      case 'qualite': return analyserQualite(lib).cle;
      case 'annee': var n = parseInt(lib, 10); return n > 0 ? String(n) : '';
    }
    return '';
  }
  function lireEtat(recherche) {
    var p = new URLSearchParams(recherche || '');
    var sel = selectionVide();
    FACETTES.forEach(function (f) {
      p.getAll(PARAMS[f]).forEach(function (v) {
        var lib = nettoyer(v);
        var c = cleDepuisLibelle(f, lib);
        if (c) sel[f].set(c, f === 'valeur' ? analyserValeur(lib).libelle : (f === 'qualite' ? analyserQualite(lib).libelle : lib));
      });
    });
    var tri = p.get('sort_by') || '';
    if (TRIS.indexOf(tri) < 0) tri = '';
    return { sel: sel, tri: tri };
  }
  function ecrireEtat(recherche, sel, tri) {
    var p = new URLSearchParams(recherche || '');
    FACETTES.forEach(function (f) { p.delete(PARAMS[f]); });
    p.delete('sort_by');
    p.delete('page');
    FACETTES.forEach(function (f) {
      sel[f].forEach(function (lib) { p.append(PARAMS[f], lib.replace(/ /g, ' ')); });
    });
    if (tri && TRIS.indexOf(tri) > -1) p.set('sort_by', tri);
    var s = p.toString();
    return s ? '?' + s : '';
  }

  return {
    FACETTES: FACETTES,
    TRIS: TRIS,
    nettoyer: nettoyer,
    sansAccent: sansAccent,
    comparerTexte: comparerTexte,
    analyserValeur: analyserValeur,
    analyserQualite: analyserQualite,
    comparerValeurs: comparerValeurs,
    comparerQualites: comparerQualites,
    rangMetal: rangMetal,
    clePiece: clePiece,
    anneeParis: anneeParis,
    modeDepuisCollection: modeDepuisCollection,
    creerRecord: creerRecord,
    comparateur: comparateur,
    estTriParDefaut: estTriParDefaut,
    trier: trier,
    grouper: grouper,
    selectionVide: selectionVide,
    nbFiltres: nbFiltres,
    filtrer: filtrer,
    calculerFacettes: calculerFacettes,
    cleDepuisLibelle: cleDepuisLibelle,
    lireEtat: lireEtat,
    ecrireEtat: ecrireEtat
  };
});
